/**
 * Marketplace React Query Hooks
 * BP-08-04: Marketplace Frontend (Family)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { marketplaceApi } from '@/features/marketplace/api/marketplaceApi';
import type {
  VendorCategory,
  Vendor,
  Product,
  ProductReview,
  Order,
  PaginatedResponse,
  MarketplaceSearchResults,
  FeaturedContent,
  VendorFilters,
  ProductFilters,
  OrderFilters,
  ReviewFilters,
  InquiryRequest,
  QuoteRequest,
  CreateReviewRequest,
} from '@/features/marketplace/types/marketplaceTypes';

// ============================================================================
// Query Keys
// ============================================================================

export const marketplaceKeys = {
  all: ['marketplace'] as const,
  categories: () => [...marketplaceKeys.all, 'categories'] as const,
  category: (slug: string) => [...marketplaceKeys.categories(), slug] as const,
  
  vendors: () => [...marketplaceKeys.all, 'vendors'] as const,
  vendorList: (filters?: VendorFilters) => [...marketplaceKeys.vendors(), 'list', filters] as const,
  vendor: (slug: string) => [...marketplaceKeys.vendors(), slug] as const,
  vendorProducts: (vendorId: string) => [...marketplaceKeys.vendors(), vendorId, 'products'] as const,
  
  products: () => [...marketplaceKeys.all, 'products'] as const,
  productList: (filters?: ProductFilters) => [...marketplaceKeys.products(), 'list', filters] as const,
  product: (slug: string) => [...marketplaceKeys.products(), slug] as const,
  
  featured: () => [...marketplaceKeys.all, 'featured'] as const,
  search: (query: string) => [...marketplaceKeys.all, 'search', query] as const,
  
  reviews: (productId: string) => [...marketplaceKeys.all, 'reviews', productId] as const,
  reviewList: (productId: string, filters?: ReviewFilters) => 
    [...marketplaceKeys.reviews(productId), 'list', filters] as const,
  
  orders: (familyId: string) => [...marketplaceKeys.all, 'orders', familyId] as const,
  orderList: (familyId: string, filters?: OrderFilters) => 
    [...marketplaceKeys.orders(familyId), 'list', filters] as const,
  order: (orderId: string) => [...marketplaceKeys.all, 'order', orderId] as const,
};

// ============================================================================
// Category Hooks
// ============================================================================

export const useCategories = (
  options?: Omit<UseQueryOptions<VendorCategory[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.categories(),
    queryFn: marketplaceApi.getCategories,
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  });
};

export const useCategory = (
  slug: string,
  options?: Omit<UseQueryOptions<VendorCategory, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.category(slug),
    queryFn: () => marketplaceApi.getCategory(slug),
    enabled: !!slug,
    ...options,
  });
};

// ============================================================================
// Vendor Hooks
// ============================================================================

export const useVendors = (
  filters?: VendorFilters,
  page = 1,
  pageSize = 12,
  options?: Omit<UseQueryOptions<PaginatedResponse<Vendor>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...marketplaceKeys.vendorList(filters), page, pageSize],
    queryFn: () => marketplaceApi.getVendors(filters, page, pageSize),
    ...options,
  });
};

export const useInfiniteVendors = (
  filters?: VendorFilters,
  pageSize = 12
) => {
  return useInfiniteQuery({
    queryKey: marketplaceKeys.vendorList(filters),
    queryFn: ({ pageParam = 1 }) => marketplaceApi.getVendors(filters, pageParam, pageSize),
    getNextPageParam: (lastPage) => 
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  });
};

export const useVendor = (
  slug: string,
  options?: Omit<UseQueryOptions<Vendor, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.vendor(slug),
    queryFn: () => marketplaceApi.getVendor(slug),
    enabled: !!slug,
    ...options,
  });
};

export const useVendorProducts = (
  vendorId: string,
  page = 1,
  pageSize = 12,
  options?: Omit<UseQueryOptions<PaginatedResponse<Product>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...marketplaceKeys.vendorProducts(vendorId), page, pageSize],
    queryFn: () => marketplaceApi.getVendorProducts(vendorId, page, pageSize),
    enabled: !!vendorId,
    ...options,
  });
};

// ============================================================================
// Product Hooks
// ============================================================================

export const useProducts = (
  filters?: ProductFilters,
  page = 1,
  pageSize = 12,
  options?: Omit<UseQueryOptions<PaginatedResponse<Product>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...marketplaceKeys.productList(filters), page, pageSize],
    queryFn: () => marketplaceApi.getProducts(filters, page, pageSize),
    ...options,
  });
};

export const useInfiniteProducts = (
  filters?: ProductFilters,
  pageSize = 12
) => {
  return useInfiniteQuery({
    queryKey: marketplaceKeys.productList(filters),
    queryFn: ({ pageParam = 1 }) => marketplaceApi.getProducts(filters, pageParam, pageSize),
    getNextPageParam: (lastPage) => 
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  });
};

export const useProduct = (
  slug: string,
  options?: Omit<UseQueryOptions<Product, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.product(slug),
    queryFn: () => marketplaceApi.getProduct(slug),
    enabled: !!slug,
    ...options,
  });
};

// ============================================================================
// Featured & Search Hooks
// ============================================================================

export const useFeatured = (
  options?: Omit<UseQueryOptions<FeaturedContent, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.featured(),
    queryFn: marketplaceApi.getFeatured,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

export const useMarketplaceSearch = (
  query: string,
  limit = 10,
  options?: Omit<UseQueryOptions<MarketplaceSearchResults, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.search(query),
    queryFn: () => marketplaceApi.search(query, limit),
    enabled: query.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutes
    ...options,
  });
};

// ============================================================================
// Review Hooks
// ============================================================================

export const useProductReviews = (
  productId: string,
  filters?: ReviewFilters,
  page = 1,
  pageSize = 10,
  options?: Omit<UseQueryOptions<PaginatedResponse<ProductReview>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...marketplaceKeys.reviewList(productId, filters), page, pageSize],
    queryFn: () => marketplaceApi.getProductReviews(productId, filters, page, pageSize),
    enabled: !!productId,
    ...options,
  });
};

export const useInfiniteReviews = (
  productId: string,
  filters?: ReviewFilters,
  pageSize = 10
) => {
  return useInfiniteQuery({
    queryKey: marketplaceKeys.reviewList(productId, filters),
    queryFn: ({ pageParam = 1 }) => 
      marketplaceApi.getProductReviews(productId, filters, pageParam, pageSize),
    getNextPageParam: (lastPage) => 
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
    enabled: !!productId,
  });
};

export const useCreateReview = (
  options?: UseMutationOptions<ProductReview, Error, CreateReviewRequest>
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: marketplaceApi.createReview,
    onSuccess: (review) => {
      // Invalidate reviews for the product
      queryClient.invalidateQueries({ 
        queryKey: marketplaceKeys.reviews(review.productId) 
      });
      // Invalidate product to update review count/rating
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.products(),
      });
    },
    ...options,
  });
};

export const useMarkReviewHelpful = (
  productId: string,
  options?: UseMutationOptions<
    { helpfulCount: number; isHelpfulByCurrentUser: boolean },
    Error,
    string
  >
) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (reviewId: string) => marketplaceApi.markReviewHelpful(reviewId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.reviews(productId),
      });
    },
    ...options,
  });
};

// ============================================================================
// Inquiry & Quote Hooks
// ============================================================================

export const useSubmitInquiry = (
  options?: UseMutationOptions<
    { success: boolean; message: string },
    Error,
    InquiryRequest
  >
) => {
  return useMutation({
    mutationFn: marketplaceApi.submitInquiry,
    ...options,
  });
};

export const useRequestQuote = (
  options?: UseMutationOptions<
    { success: boolean; message: string; quoteId: string },
    Error,
    QuoteRequest
  >
) => {
  return useMutation({
    mutationFn: marketplaceApi.requestQuote,
    ...options,
  });
};

// ============================================================================
// Order Hooks
// ============================================================================

export const useOrders = (
  familyId: string,
  filters?: OrderFilters,
  page = 1,
  pageSize = 10,
  options?: Omit<UseQueryOptions<PaginatedResponse<Order>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [...marketplaceKeys.orderList(familyId, filters), page, pageSize],
    queryFn: () => marketplaceApi.getOrders(familyId, filters, page, pageSize),
    enabled: !!familyId,
    ...options,
  });
};

export const useOrder = (
  orderId: string,
  options?: Omit<UseQueryOptions<Order, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: marketplaceKeys.order(orderId),
    queryFn: () => marketplaceApi.getOrder(orderId),
    enabled: !!orderId,
    ...options,
  });
};

// ============================================================================
// Prefetch Helpers
// ============================================================================

export const usePrefetchProduct = () => {
  const queryClient = useQueryClient();
  
  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: marketplaceKeys.product(slug),
      queryFn: () => marketplaceApi.getProduct(slug),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };
};

export const usePrefetchVendor = () => {
  const queryClient = useQueryClient();
  
  return (slug: string) => {
    queryClient.prefetchQuery({
      queryKey: marketplaceKeys.vendor(slug),
      queryFn: () => marketplaceApi.getVendor(slug),
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };
};

